<template>
  <v-card flat>
    <v-card flat class="p7-5 pa-md-5 bg-pwt_snow">
      <v-card-title> Unsaved trips </v-card-title>
      <v-card-text>
        <UnsavedTripCarousel />
      </v-card-text>
      <v-card-title> Saved trips </v-card-title>
      <v-text-field
        v-model="searchValue"
        bg-color="background"
        clearable
        placeholder="Search saved trips..."
        class="mx-4"
      />
      <v-row no-gutters>
        <v-col
          v-for="(trip, i) in filteredSavedTrips"
          :key="'saved-trip-' + i"
          cols="12"
          md="6"
        >
          <div :class="mdAndUp ? (i % 2 === 0 ? 'pr-1' : 'pl-1') : ''">
            <SavedTripCard
              :trip="trip"
              :style="mdAndUp ? 'height: 100%' : ''"
              emitOnly
              @view-trip="() => setShowingTrip(trip)"
            />
          </div>
        </v-col>
      </v-row>
      <v-card v-if="!savedTrips.length" flat>
        <v-card-text class="pt-0">
          You currently have no saved trips.
        </v-card-text>
      </v-card>
      <v-card v-else-if="!filteredSavedTrips.length && searchValue" flat>
        <v-card-text class="pt-0">
          No trips match your search query.
        </v-card-text>
      </v-card>
    </v-card>
  </v-card>
</template>
<script lang="ts" setup>
import SavedTripCard from "@/components/trips/history/SavedTripCard.vue";
import { useTripStore } from "@/domains/trips/tripStore.ts";
import type { Trip } from "@/domains/trips/types.ts";
import { useDashboardNavStore } from "@/stores/dashboardNavStore.ts";
import { computed, ref } from "vue";
import { useDisplay } from "vuetify";
import UnsavedTripCarousel from "../../trips/history/UnsavedTripCarousel.vue";

const { mdAndUp } = useDisplay();

const tripStore = useTripStore();
const dashboardNavStore = useDashboardNavStore();

const setShowingTrip = (trip: Trip) => {
  dashboardNavStore.showTripDetails(trip.id);
};

const savedTrips = computed<Trip[]>(() => tripStore.getSavedTrips);

const searchValue = ref<string | undefined>(undefined);
const filteredSavedTrips = computed<Trip[]>((): Trip[] => {
  if (!searchValue.value) return savedTrips.value;
  const sanitizedSearchValue = (searchValue.value || "")
    .toString()
    .toLocaleLowerCase()
    .trim();
  return savedTrips.value.filter((trip) => {
    // check for trip name match
    if (trip.name && trip.name.toLowerCase().includes(sanitizedSearchValue))
      return true;

    // check for origin match
    if (trip.origin) {
      if (
        trip.origin.address &&
        trip.origin.address.toLowerCase().includes(sanitizedSearchValue)
      )
        return true;
      if (
        trip.origin.name &&
        trip.origin.name.toLowerCase().includes(sanitizedSearchValue)
      )
        return true;
    }

    // check for destination match
    if (trip.destination) {
      if (
        trip.destination.address &&
        trip.destination.address.toLowerCase().includes(sanitizedSearchValue)
      )
        return true;
      if (
        trip.destination.name &&
        trip.destination.name.toLowerCase().includes(sanitizedSearchValue)
      )
        return true;
    }

    // check for waypoint match
    for (const waypoint of trip.waypoints) {
      if (
        waypoint.address &&
        waypoint.address.toLowerCase().includes(sanitizedSearchValue)
      )
        return true;
      if (
        waypoint.name &&
        waypoint.name.toLowerCase().includes(sanitizedSearchValue)
      )
        return true;
    }

    // no match found
    return false;
  });
});
</script>
